// src/pages/pmc/PmcEngineerMeasurementSetupPane.js

import { useEffect, useMemo, useState } from "react";
import { db } from "../../firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
  getDoc,
  deleteDoc,
} from "firebase/firestore";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";

const cx = (...xs) => xs.filter(Boolean).join(" ");

function clean(v = "") {
  return String(v || "").trim().toLowerCase();
}

function formatDate(ts) {
  try {
    if (!ts) return "—";
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function getState(x = {}) {
  return x.state || x.stateName || x.projectState || x.pacsState || "";
}

function getDistrict(x = {}) {
  return (
    x.district ||
    x.districtName ||
    x.projectDistrict ||
    x.pacsDistrict ||
    x.city ||
    ""
  );
}

function isStrictNafed(x = {}) {
  return (
    x.nafedProject === true &&
    x.projectSource === "NAFED" &&
    x.applicationPortal === "NAFED_PACS_APPLY"
  );
}

function applyNafedScopeToProject(project, nafedScope) {
  if (!nafedScope?.onlyNafedApply && !nafedScope?.strictNafedApplyOnly) {
    return true;
  }

  const scopeState = clean(nafedScope?.state);

  const allowedDistricts = (
    nafedScope?.allowedDistricts ||
    nafedScope?.districts ||
    []
  ).map(clean);

  const stateOk = !scopeState || clean(getState(project)) === scopeState;

  const districtOk =
    !allowedDistricts.length ||
    allowedDistricts.includes(clean(getDistrict(project)));

  return isStrictNafed(project) && stateOk && districtOk;
}

function tenderMatchesAssignedProject(tender, assignedProjectIds, assignedApplicationIds) {
  const possibleProjectIds = [
    tender.projectId,
    tender.workflowProjectId,
    tender.wfProjectId,
    tender.godownProjectId,
    tender.godownApplicationDocId,
    tender.applicationDocId,
    tender.linkedProjectId,
    tender.linkedGodownProjectId,
  ].filter(Boolean);

  const possibleApplicationIds = [
    tender.applicationId,
    tender.godownApplicationId,
    tender.linkedApplicationId,
    tender.pacsApplicationId,
  ].filter(Boolean);

  return (
    possibleProjectIds.some((id) => assignedProjectIds.has(String(id))) ||
    possibleApplicationIds.some((id) => assignedApplicationIds.has(String(id)))
  );
}

function Badge({ type = "pending", children }) {
  return <span className={`ms-badge ${type}`}>{children}</span>;
}

function StatCard({ label, value, note }) {
  return (
    <div className="ms-stat-card">
      <label>{label}</label>
      <b>{value}</b>
      {note && <small>{note}</small>}
    </div>
  );
}

function exportStatusCsv(rows) {
  const headers = [
    "Sr",
    "Project ID",
    "PACS",
    "Project",
    "Location",
    "Contractor",
    "Status",
    "Rows",
    "Columns",
    "Updated",
  ];

  const data = rows.map((r, i) => [
    i + 1,
    r.id,
    r.pacsName,
    r.title,
    r.location,
    r.awardedVendorName,
    r.status,
    r.rowCount,
    r.columnCount,
    formatDate(r.updatedAt),
  ]);

  const csv = [headers, ...data]
    .map((row) =>
      row.map((v) => `"${String(v || "").replace(/"/g, '""')}"`).join(",")
    )
    .join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = "NAFED_Measurement_Sheet_Status.csv";
  a.click();

  URL.revokeObjectURL(url);
}

function exportStatusPdf(rows) {
  const pdf = new jsPDF("l", "pt", "a4");
  const pageWidth = pdf.internal.pageSize.getWidth();
  let y = 42;

  pdf.setFillColor(6, 43, 85);
  pdf.rect(0, 0, pageWidth, 55, "F");

  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(15);
  pdf.text("NAFED Measurement Sheet Status Report", 40, 34);

  pdf.setTextColor(0, 0, 0);
  pdf.setFontSize(8);
  y = 80;

  const headers = [
    "Sr",
    "PACS",
    "Project",
    "Location",
    "Contractor",
    "Status",
    "Rows",
    "Cols",
    "Updated",
  ];

  const widths = [28, 150, 170, 90, 140, 75, 45, 45, 90];

  const drawHeader = () => {
    let x = 28;
    pdf.setFillColor(226, 232, 240);
    pdf.setFont("helvetica", "bold");

    headers.forEach((h, i) => {
      pdf.rect(x, y, widths[i], 24, "FD");
      pdf.text(h, x + 4, y + 15);
      x += widths[i];
    });

    y += 24;
    pdf.setFont("helvetica", "normal");
  };

  drawHeader();

  rows.forEach((r, idx) => {
    if (y > 545) {
      pdf.addPage();
      y = 40;
      drawHeader();
    }

    const values = [
      idx + 1,
      r.pacsName || "—",
      r.title || "—",
      r.location || "—",
      r.awardedVendorName || "—",
      r.status || "—",
      r.rowCount || 0,
      r.columnCount || 0,
      formatDate(r.updatedAt),
    ];

    let x = 28;

    values.forEach((v, i) => {
      pdf.rect(x, y, widths[i], 24);
      pdf.text(String(v).slice(0, i === 2 ? 26 : 18), x + 4, y + 15);
      x += widths[i];
    });

    y += 24;
  });

  pdf.save("NAFED_Measurement_Sheet_Status.pdf");
}

export default function PmcEngineerMeasurementSetupPane({ nafedScope }) {
  const [tenders, setTenders] = useState([]);
  const [loadingTenders, setLoadingTenders] = useState(true);

  const [selectedTenderId, setSelectedTenderId] = useState("");

  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [saving, setSaving] = useState(false);

  const [hasSavedSheet, setHasSavedSheet] = useState(false);
  const [loadingSheet, setLoadingSheet] = useState(false);

  const [measurementStatusRows, setMeasurementStatusRows] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [statusSearch, setStatusSearch] = useState("");

  const isNafedMode =
    nafedScope?.onlyNafedApply || nafedScope?.strictNafedApplyOnly;

  const normalizeLoadedRows = (loadedRows = [], loadedColumns = []) => {
    if (!Array.isArray(loadedRows)) return [];

    return loadedRows.map((r) => {
      if (Array.isArray(r)) {
        const obj = {};
        loadedColumns.forEach((c, i) => {
          obj[c] = r[i] ?? "";
        });
        return obj;
      }

      return r || {};
    });
  };

  const getNafedAssignedProjects = async () => {
    const snap = await getDocs(collection(db, "godown_projects"));

    const assignedProjects = snap.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((p) => applyNafedScopeToProject(p, nafedScope));

    const projectIds = new Set(assignedProjects.map((p) => String(p.id)));

    const applicationIds = new Set(
      assignedProjects
        .map((p) => String(p.applicationId || ""))
        .filter(Boolean)
    );

    return { assignedProjects, projectIds, applicationIds };
  };

  const loadSavedSheetForTender = async (tid) => {
    setRows([]);
    setColumns([]);
    setHasSavedSheet(false);

    if (!tid) return null;

    try {
      setLoadingSheet(true);

      const ref = doc(
        db,
        "tenders",
        tid,
        "measurement_sheets",
        "FULL_MEASUREMENT_SHEET"
      );

      const snap = await getDoc(ref);

      if (snap.exists()) {
        const data = snap.data() || {};
        const loadedColumns = Array.isArray(data.columns) ? data.columns : [];
        const loadedRows = Array.isArray(data.rows) ? data.rows : [];

        setColumns(loadedColumns);
        setRows(normalizeLoadedRows(loadedRows, loadedColumns));
        setHasSavedSheet(true);

        return {
          exists: true,
          columns: loadedColumns,
          rows: loadedRows,
          updatedAt: data.updatedAt,
          createdAt: data.createdAt,
        };
      }

      setHasSavedSheet(false);
      return { exists: false, columns: [], rows: [] };
    } catch (e) {
      console.error(e);
      toast.error("Existing measurement sheet load failed.");
      return null;
    } finally {
      setLoadingSheet(false);
    }
  };

  const loadMeasurementStatusForAll = async (awardedList) => {
    try {
      setLoadingStatus(true);

      const statusList = [];

      for (const t of awardedList) {
        const ref = doc(
          db,
          "tenders",
          t.id,
          "measurement_sheets",
          "FULL_MEASUREMENT_SHEET"
        );

        const snap = await getDoc(ref);

        let itemCount = 0;
        let columnCount = 0;
        let updatedAt = null;
        let uploaded = false;

        if (snap.exists()) {
          const data = snap.data() || {};
          const savedRows = Array.isArray(data.rows) ? data.rows : [];
          const savedColumns = Array.isArray(data.columns) ? data.columns : [];

          itemCount = savedRows.length;
          columnCount = savedColumns.length;
          updatedAt = data.updatedAt || data.createdAt || null;
          uploaded = itemCount > 0 || columnCount > 0;
        }

        statusList.push({
          id: t.id,
          title: t.title || t.projectName || t.workName || "Untitled Project",
          pacsName:
            t.ownerCompany ||
            t.pacsName ||
            t.pacsShortName ||
            t.societyName ||
            "—",
          location: t.city || t.district || t.projectDistrict || "—",
          awardedVendorUid: t.awardedVendorUid || "",
          awardedVendorName:
            t.awardedVendorName ||
            t.vendorName ||
            t.contractorName ||
            "—",
          rowCount: itemCount,
          columnCount,
          updatedAt,
          status: uploaded ? "UPLOADED" : "PENDING",
          raw: t,
        });
      }

      statusList.sort((a, b) => {
        if (a.status !== b.status) return a.status === "PENDING" ? -1 : 1;
        return String(a.pacsName || "").localeCompare(String(b.pacsName || ""));
      });

      setMeasurementStatusRows(statusList);
    } catch (e) {
      console.error(e);
      toast.error("Measurement status table loading failed.");
    } finally {
      setLoadingStatus(false);
    }
  };

  const loadTenders = async () => {
    try {
      setLoadingTenders(true);

      const snap = await getDocs(collection(db, "tenders"));
      let list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

      list = list.filter((t) => t.awardedVendorUid);

      if (isNafedMode) {
        const { projectIds, applicationIds } = await getNafedAssignedProjects();

        const scopeState = clean(nafedScope?.state);
        const allowedDistricts = (
          nafedScope?.allowedDistricts ||
          nafedScope?.districts ||
          []
        ).map(clean);

        list = list.filter((t) => {
          const linked = tenderMatchesAssignedProject(t, projectIds, applicationIds);

          const tenderStrict =
            t.nafedProject === true &&
            t.projectSource === "NAFED";

          const stateOk = !scopeState || clean(getState(t)) === scopeState;

          const districtOk =
            !allowedDistricts.length ||
            allowedDistricts.includes(clean(getDistrict(t)));

          return linked || (tenderStrict && stateOk && districtOk);
        });
      }

      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

      setTenders(list);
      await loadMeasurementStatusForAll(list);
    } catch (e) {
      console.error(e);
      toast.error("Projects load failed.");
    } finally {
      setLoadingTenders(false);
    }
  };

  useEffect(() => {
    loadTenders();
  }, [JSON.stringify(nafedScope || {})]);

  const selectedTender = useMemo(
    () => tenders.find((t) => t.id === selectedTenderId) || null,
    [tenders, selectedTenderId]
  );

  const summary = useMemo(() => {
    const total = measurementStatusRows.length;
    const uploaded = measurementStatusRows.filter(
      (x) => x.status === "UPLOADED"
    ).length;
    const pending = measurementStatusRows.filter(
      (x) => x.status === "PENDING"
    ).length;
    const totalRows = measurementStatusRows.reduce(
      (sum, x) => sum + Number(x.rowCount || 0),
      0
    );
    const totalColumns = measurementStatusRows.reduce(
      (sum, x) => sum + Number(x.columnCount || 0),
      0
    );

    return { total, uploaded, pending, totalRows, totalColumns };
  }, [measurementStatusRows]);

  const filteredStatusRows = useMemo(() => {
    let list = [...measurementStatusRows];

    if (statusFilter !== "all") {
      list = list.filter((x) => x.status === statusFilter);
    }

    const needle = statusSearch.trim().toLowerCase();

    if (needle) {
      list = list.filter((x) => {
        const hay = [
          x.title,
          x.pacsName,
          x.location,
          x.awardedVendorName,
          x.id,
        ]
          .map((v) => String(v || "").toLowerCase())
          .join("|");

        return hay.includes(needle);
      });
    }

    return list;
  }, [measurementStatusRows, statusFilter, statusSearch]);
    const handleSelectTender = async (id) => {
    setSelectedTenderId(id);
    await loadSavedSheetForTender(id);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!selectedTenderId) {
      toast.error("Pehle project select karein.");
      e.target.value = "";
      return;
    }

    try {
      setSaving(true);

      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];

      const rawRows = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        defval: "",
        raw: false,
        blankrows: true,
      });

      const filteredRows = rawRows.filter(
        (r) =>
          Array.isArray(r) &&
          r.some((cell) => String(cell || "").trim() !== "")
      );

      if (!filteredRows.length) {
        toast.error("Excel me koi row nahi mili.");
        setRows([]);
        setColumns([]);
        return;
      }

      const maxCols = Math.max(...filteredRows.map((r) => r.length));

      const defaultHeaders = [
        "ITEM NO",
        "ITEM DESCRIPTION",
        "NOS",
        "LENGTH",
        "WIDTH",
        "DEPTH",
        "QTY.",
        "QUANTITY",
      ];

      const cols = Array.from({ length: maxCols }, (_, i) => {
        return defaultHeaders[i] || `COL ${i + 1}`;
      });

      const objectRows = filteredRows.map((arr) => {
        const obj = {};
        cols.forEach((col, i) => {
          obj[col] = arr[i] ?? "";
        });
        return obj;
      });

      setColumns(cols);
      setRows(objectRows);
      setHasSavedSheet(false);

      toast.success(
        `Excel se ${objectRows.length} rows aur ${cols.length} columns load ho gaye.`
      );
    } catch (err) {
      console.error(err);
      toast.error("Measurement Excel read karte waqt error aya.");
      setRows([]);
      setColumns([]);
    } finally {
      setSaving(false);
      e.target.value = "";
    }
  };

  const handleSave = async () => {
    if (!selectedTenderId) {
      toast.error("Project select karein.");
      return;
    }

    if (!rows.length) {
      toast.error("Pehle Excel upload karke rows load karein.");
      return;
    }

    try {
      setSaving(true);

      await setDoc(
        doc(
          db,
          "tenders",
          selectedTenderId,
          "measurement_sheets",
          "FULL_MEASUREMENT_SHEET"
        ),
        {
          tenderId: selectedTenderId,
          columns,
          rows,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );

      setHasSavedSheet(true);

      toast.success("Full measurement sheet save ho gayi.");

      await loadTenders();
      await loadSavedSheetForTender(selectedTenderId);
    } catch (e) {
      console.error("SAVE ERROR:", e);
      toast.error(e?.message || "Measurement sheet save karte waqt error aya.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTenderId) {
      toast.error("Project select karein.");
      return;
    }

    if (!hasSavedSheet) {
      toast.error("Is project ke liye koi saved sheet nahi mili.");
      return;
    }

    const ok = window.confirm(
      "Kya aap is project ki poori measurement sheet delete karna chahte hain? Ye action undo nahi hoga."
    );

    if (!ok) return;

    try {
      setSaving(true);

      await deleteDoc(
        doc(
          db,
          "tenders",
          selectedTenderId,
          "measurement_sheets",
          "FULL_MEASUREMENT_SHEET"
        )
      );

      setRows([]);
      setColumns([]);
      setHasSavedSheet(false);

      toast.success("Measurement sheet delete ho gayi.");

      await loadTenders();
    } catch (e) {
      console.error(e);
      toast.error("Measurement sheet delete karte waqt error aya.");
    } finally {
      setSaving(false);
    }
  };

  const quickSelectFromTable = async (row) => {
    await handleSelectTender(row.id);

    setTimeout(() => {
      const el = document.getElementById("measurement-upload-panel");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  return (
    <div className="ms-page">
      <style>{`
        .ms-page {
          display: grid;
          gap: 18px;
          font-family: Inter, "Segoe UI", Arial, sans-serif;
        }

        .ms-hero {
          background: linear-gradient(135deg, #062b55, #064e3b);
          color: white;
          border-radius: 22px;
          padding: 24px;
          box-shadow: 0 18px 40px rgba(6,43,85,.20);
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 18px;
        }

        .ms-hero small {
          display: inline-block;
          color: #bbf7d0;
          font-size: 11px;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .18em;
        }

        .ms-hero h2 {
          margin: 6px 0 0;
          font-size: 26px;
          font-weight: 950;
        }

        .ms-hero p {
          margin: 8px 0 0;
          color: #d1fae5;
          font-size: 13px;
          line-height: 1.7;
          max-width: 760px;
        }

        .ms-btn {
          border: 0;
          border-radius: 11px;
          padding: 10px 14px;
          font-size: 12px;
          font-weight: 950;
          cursor: pointer;
          white-space: nowrap;
        }

        .ms-btn.white {
          background: white;
          color: #064e3b;
        }

        .ms-btn.primary {
          background: #062b55;
          color: white;
        }

        .ms-btn.green {
          background: #16a34a;
          color: white;
        }

        .ms-btn.red {
          background: #ef4444;
          color: white;
        }

        .ms-btn.gray {
          background: #e2e8f0;
          color: #071f45;
          border: 1px solid #cbd5e1;
        }

        .ms-btn:disabled {
          opacity: .45;
          cursor: not-allowed;
        }

        .ms-stats {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 12px;
        }

        .ms-stat-card {
          background: white;
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          padding: 16px;
          box-shadow: 0 8px 22px rgba(15,23,42,.05);
        }

        .ms-stat-card label {
          display: block;
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .12em;
        }

        .ms-stat-card b {
          display: block;
          margin-top: 8px;
          color: #071f45;
          font-size: 24px;
          font-weight: 950;
        }

        .ms-stat-card small {
          display: block;
          margin-top: 5px;
          color: #64748b;
          font-size: 11px;
          font-weight: 750;
        }

        .ms-panel {
          background: white;
          border: 1.5px solid #111827;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 8px 22px rgba(15,23,42,.06);
        }

        .ms-panel-head {
          padding: 15px 18px;
          background: #f8fafc;
          border-bottom: 1.5px solid #111827;
          display: flex;
          justify-content: space-between;
          gap: 14px;
          align-items: center;
        }

        .ms-panel-head h3 {
          margin: 0;
          color: #071f45;
          font-size: 17px;
          font-weight: 950;
        }

        .ms-panel-head p {
          margin: 4px 0 0;
          color: #64748b;
          font-size: 12px;
          font-weight: 750;
        }

        .ms-filter {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .ms-input {
          height: 38px;
          border: 1px solid #cbd5e1;
          border-radius: 10px;
          padding: 0 10px;
          font-size: 12px;
          font-weight: 800;
          background: white;
          color: #071f45;
          outline: none;
        }

        .ms-table-wrap {
          overflow-x: auto;
        }

        .ms-table {
          width: 100%;
          min-width: 1200px;
          border-collapse: collapse;
          font-size: 12px;
        }

        .ms-table th {
          background: #e2e8f0;
          color: #071f45;
          text-align: left;
          padding: 12px;
          border: 1.5px solid #111827;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: .08em;
          font-weight: 950;
          white-space: nowrap;
        }

        .ms-table td {
          padding: 12px;
          border: 1.5px solid #111827;
          vertical-align: top;
          background: white;
          color: #0f172a;
          font-weight: 750;
        }

        .ms-table tbody tr:nth-child(even) td {
          background: #f8fafc;
        }

        .ms-table tbody tr:hover td {
          background: #eef6ff;
        }

        .ms-title {
          color: #071f45;
          font-weight: 950;
        }

        .muted {
          color: #64748b;
          font-size: 11px;
          line-height: 1.6;
          font-weight: 750;
        }

        .ms-badge {
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 10px;
          font-weight: 950;
          display: inline-block;
          white-space: nowrap;
        }

        .ms-badge.uploaded {
          background: #dcfce7;
          color: #166534;
          border: 1px solid #16a34a;
        }

        .ms-badge.pending {
          background: #fef3c7;
          color: #92400e;
          border: 1px solid #f59e0b;
        }

        .ms-upload-grid {
          display: grid;
          grid-template-columns: 1.4fr .8fr;
          gap: 18px;
          padding: 18px;
        }

        .ms-box {
          border: 1px solid #dbe4ef;
          border-radius: 16px;
          background: #f8fafc;
          padding: 16px;
        }

        .ms-box h4 {
          margin: 0 0 12px;
          color: #071f45;
          font-size: 15px;
          font-weight: 950;
        }

        .ms-field {
          display: grid;
          gap: 6px;
          margin-bottom: 12px;
        }

        .ms-field label {
          font-size: 10px;
          color: #64748b;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: .08em;
        }

        .ms-field select,
        .ms-field input {
          width: 100%;
          height: 40px;
          border: 1px solid #cbd5e1;
          border-radius: 11px;
          padding: 0 10px;
          font-size: 12px;
          font-weight: 800;
          outline: none;
          background: white;
        }

        .selected-card {
          background: white;
          border: 1px solid #cbd5e1;
          border-radius: 14px;
          padding: 14px;
          font-size: 12px;
          line-height: 1.8;
          color: #334155;
        }

        .empty {
          padding: 35px;
          text-align: center;
          color: #64748b;
          font-weight: 850;
        }

        @media(max-width:1300px) {
          .ms-stats {
            grid-template-columns: repeat(2, 1fr);
          }

          .ms-upload-grid {
            grid-template-columns: 1fr;
          }
        }

        @media(max-width:700px) {
          .ms-stats {
            grid-template-columns: 1fr;
          }

          .ms-hero,
          .ms-panel-head {
            flex-direction: column;
            align-items: flex-start;
          }
        }
      `}</style>

      <section className="ms-hero">
        <div>
          <small>Engineer Measurement Automation</small>
          <h2>Measurement Sheet Control Room</h2>
          <p>
            Project-wise measurement sheet upload, pending status and saved
            sheet verification. In NAFED PMC mode, only projects within this
            PMC&apos;s allocated state/district scope are visible.
          </p>
        </div>

        <button type="button" onClick={loadTenders} className="ms-btn white">
          Refresh Status
        </button>
      </section>

      <section className="ms-stats">
        <StatCard label="Awarded Projects" value={summary.total} note="Scoped tenders" />
        <StatCard label="Sheet Uploaded" value={summary.uploaded} note="Template ready" />
        <StatCard label="Sheet Pending" value={summary.pending} note="Upload required" />
        <StatCard label="Total Rows" value={summary.totalRows} note="Saved rows" />
        <StatCard label="Total Columns" value={summary.totalColumns} note="Saved columns" />
      </section>

      <section className="ms-panel">
        <div className="ms-panel-head">
          <div>
            <h3>Project-wise Measurement Sheet Status</h3>
            <p>Professional black-line table with NAFED scoped projects.</p>
          </div>

          <div className="ms-filter">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="ms-input"
            >
              <option value="all">All Status</option>
              <option value="UPLOADED">Uploaded</option>
              <option value="PENDING">Pending</option>
            </select>

            <input
              value={statusSearch}
              onChange={(e) => setStatusSearch(e.target.value)}
              placeholder="Search PACS / project / vendor..."
              className="ms-input"
            />

            <button
              className="ms-btn green"
              onClick={() => exportStatusCsv(filteredStatusRows)}
            >
              Excel / CSV
            </button>

            <button
              className="ms-btn primary"
              onClick={() => exportStatusPdf(filteredStatusRows)}
            >
              PDF
            </button>
          </div>
        </div>

        {loadingStatus || loadingTenders ? (
          <div className="empty">Measurement status loading...</div>
        ) : filteredStatusRows.length === 0 ? (
          <div className="empty">No project found in current scope.</div>
        ) : (
          <div className="ms-table-wrap">
            <table className="ms-table">
              <thead>
                <tr>
                  <th>Sr</th>
                  <th>PACS / Project</th>
                  <th>Location</th>
                  <th>Contractor</th>
                  <th>Status</th>
                  <th>Rows</th>
                  <th>Columns</th>
                  <th>Updated</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredStatusRows.map((r, idx) => (
                  <tr key={r.id}>
                    <td>{idx + 1}</td>

                    <td>
                      <div className="ms-title">{r.pacsName}</div>
                      <div className="muted">{r.title}</div>
                      <div className="muted">ID: {r.id}</div>
                    </td>

                    <td>{r.location}</td>

                    <td>
                      <div className="ms-title">{r.awardedVendorName}</div>
                      <div className="muted">{r.awardedVendorUid || "—"}</div>
                    </td>

                    <td>
                      {r.status === "UPLOADED" ? (
                        <Badge type="uploaded">UPLOADED</Badge>
                      ) : (
                        <Badge type="pending">PENDING</Badge>
                      )}
                    </td>

                    <td style={{ textAlign: "right" }}>{r.rowCount}</td>

                    <td style={{ textAlign: "right" }}>{r.columnCount}</td>

                    <td>{formatDate(r.updatedAt)}</td>

                    <td>
                      <button
                        type="button"
                        onClick={() => quickSelectFromTable(r)}
                        className={cx(
                          "ms-btn",
                          r.status === "UPLOADED" ? "primary" : "green"
                        )}
                      >
                        {r.status === "UPLOADED" ? "View / Replace" : "Upload Now"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section id="measurement-upload-panel" className="ms-panel">
        <div className="ms-panel-head">
          <div>
            <h3>Upload / Replace Measurement Sheet</h3>
            <p>Full Excel sheet as-is save hogi. Engineer side same columns available rahenge.</p>
          </div>
        </div>

        <div className="ms-upload-grid">
          <div className="ms-box">
            <h4>Project Selection</h4>

            <div className="ms-field">
              <label>Select Awarded Project / Tender</label>

              <select
                value={selectedTenderId}
                onChange={(e) => handleSelectTender(e.target.value)}
                disabled={loadingTenders}
              >
                <option value="">— Select Project —</option>

                {tenders.map((t) => (
                  <option key={t.id} value={t.id}>
                    {(t.ownerCompany || t.pacsName || "PACS") +
                      " · " +
                      (t.title || t.projectName || "Untitled") +
                      " · " +
                      (t.city || t.district || "")}
                  </option>
                ))}
              </select>
            </div>

            {selectedTender && (
              <div className="selected-card">
                <b>PACS:</b>{" "}
                {selectedTender.ownerCompany || selectedTender.pacsName || "—"}
                <br />
                <b>Title:</b>{" "}
                {selectedTender.title || selectedTender.projectName || "—"}
                <br />
                <b>Location:</b>{" "}
                {selectedTender.city || selectedTender.district || "—"}
                <br />
                <b>Awarded Vendor:</b>{" "}
                {selectedTender.awardedVendorName ||
                  selectedTender.vendorName ||
                  "—"}
              </div>
            )}
          </div>

          <div className="ms-box">
            <h4>Excel Upload</h4>

            <div className="ms-field">
              <label>Upload Full Measurement Excel</label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
              />
            </div>

            <div className="muted">
              Excel columns as-is save honge. Jo format upload karoge, engineer
              side wahi measurement template dikhega.
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
              {hasSavedSheet && (
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={saving || !selectedTenderId}
                  className="ms-btn red"
                >
                  {saving ? "Processing..." : "Delete Sheet"}
                </button>
              )}

              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !rows.length || !selectedTenderId}
                className="ms-btn green"
              >
                {saving ? "Saving..." : "Save Full Sheet"}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="ms-panel">
        <div className="ms-panel-head">
          <div>
            <h3>Measurement Sheet Preview</h3>
            <p>
              Rows: <b>{rows.length}</b> | Columns: <b>{columns.length}</b>
            </p>
          </div>
        </div>

        {loadingSheet && selectedTenderId ? (
          <div className="empty">Existing measurement sheet loading...</div>
        ) : !rows.length ? (
          <div className="empty">
            Project select karne ke baad agar sheet saved hai to yahan dikh
            jayegi, ya nayi Excel upload karein.
          </div>
        ) : (
          <div className="ms-table-wrap" style={{ maxHeight: 460 }}>
            <table className="ms-table">
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col}>{col}</th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {rows.map((row, idx) => (
                  <tr key={idx}>
                    {columns.map((col) => (
                      <td key={col}>{String(row[col] ?? "")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}